<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="sm:flex sm:items-center mb-6">
      <div class="sm:flex-auto">
        <h1 class="text-4xl font-bold text-gray-900">Comptes comptables</h1>
        <p class="mt-2 text-sm text-gray-700">
          Récapitulatif des comptes comptables pour l'exercice fiscal <span class="font-semibold">{{ fiscalYearName }}</span>
        </p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- Summary Cards -->
    <div v-else-if="accountSummaries.length > 0" class="space-y-6">
      <!-- Total Summary Card -->
      <div class="bg-gradient-to-br from-indigo-50 to-blue-50 overflow-hidden rounded-xl border border-indigo-200 shadow">
        <div class="px-6 py-5">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-medium text-gray-900">Total général</h3>
              <p class="text-sm text-gray-600">{{ accountSummaries.length }} compte(s) comptable(s)</p>
            </div>
            <div class="text-right">
              <div class="text-3xl font-bold text-indigo-900">{{ formatAmount(totalHT) }} €</div>
              <div class="text-sm text-gray-600 mt-1">HT</div>
              <div class="text-lg font-semibold text-indigo-700 mt-2">{{ formatAmount(totalTVA) }} €</div>
              <div class="text-xs text-gray-600">TVA</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Accounts Table -->
      <div class="bg-white shadow rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900">
                Compte
              </th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Description
              </th>
              <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                Nombre de factures
              </th>
              <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                Montant HT
              </th>
              <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                TVA
              </th>
              <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                Total TTC
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="account in accountSummaries" :key="account.accountId" class="hover:bg-gray-50">
              <td class="whitespace-nowrap py-4 pl-6 pr-3 text-sm">
                <div class="flex items-center">
                  <div class="flex items-center justify-center h-10 w-16 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                    {{ account.accountId }}
                  </div>
                </div>
              </td>
              <td class="px-3 py-4 text-sm text-gray-900">
                <div class="font-medium">{{ account.accountName }}</div>
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                {{ account.invoiceCount }}
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900 text-right">
                {{ formatAmount(account.totalHT) }} €
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-600 text-right">
                {{ formatAmount(account.totalTVA) }} €
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm font-semibold text-indigo-900 text-right">
                {{ formatAmount(account.totalTTC) }} €
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-50">
            <tr>
              <td colspan="3" class="py-4 pl-6 pr-3 text-sm font-bold text-gray-900">
                Total
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-gray-900 text-right">
                {{ formatAmount(totalHT) }} €
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-gray-900 text-right">
                {{ formatAmount(totalTVA) }} €
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm font-bold text-indigo-900 text-right">
                {{ formatAmount(totalTTC) }} €
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">Aucun compte trouvé</h3>
      <p class="mt-1 text-sm text-gray-500">Aucune facture associée ou traitée pour cet exercice fiscal.</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AccountSummaryView",
  data() {
    return {
      accountSummaries: [],
      isLoading: true,
      fiscalYearName: '',
      userData: null
    }
  },
  computed: {
    totalHT() {
      return this.accountSummaries.reduce((sum, account) => sum + account.totalHT, 0);
    },
    totalTVA() {
      return this.accountSummaries.reduce((sum, account) => sum + account.totalTVA, 0);
    },
    totalTTC() {
      return this.accountSummaries.reduce((sum, account) => sum + account.totalTTC, 0);
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const token = await this.$auth.getAccessTokenSilently();
        const HTTP = axios.create({
          baseURL: `https://billing.clafoutis.eu/api`,
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Récupérer les données utilisateur pour l'exercice fiscal
        const userResponse = await HTTP.get('user');
        this.userData = userResponse.data;
        this.fiscalYearName = userResponse.data.fiscal_year.name;
        const fiscalYearId = userResponse.data.fiscal_year.id;

        // Récupérer les factures associées et traitées
        const [linkedResponse, doneResponse] = await Promise.all([
          HTTP.get('/invoice/linked'),
          HTTP.get('/invoice/done')
        ]);

        const allInvoices = [
          ...linkedResponse.data.invoices,
          ...doneResponse.data.invoices
        ];

        // Filtrer par exercice fiscal actuel
        const invoicesInFiscalYear = allInvoices.filter(invoice =>
          invoice.fiscal_year_id === fiscalYearId
        );

        // Grouper par compte comptable
        const accountsMap = new Map();

        invoicesInFiscalYear.forEach(invoice => {
          if (!invoice.billing_account) return;

          const accountId = invoice.billing_account.id;
          const accountName = invoice.billing_account.name;

          if (!accountsMap.has(accountId)) {
            accountsMap.set(accountId, {
              accountId,
              accountName,
              invoiceCount: 0,
              totalHT: 0,
              totalTVA: 0,
              totalTTC: 0
            });
          }

          const account = accountsMap.get(accountId);
          const amountHT = parseFloat(invoice.amount || 0);
          const amountTVA = parseFloat(invoice.amount_tax || 0);

          account.invoiceCount++;
          account.totalHT += amountHT;
          account.totalTVA += amountTVA;
          account.totalTTC += amountHT + amountTVA;
        });

        // Convertir en tableau et trier par ID
        this.accountSummaries = Array.from(accountsMap.values()).sort((a, b) => {
          const idA = parseInt(a.accountId);
          const idB = parseInt(b.accountId);
          return idA - idB;
        });

      } catch (error) {
        console.error('Error fetching account data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    formatAmount(amount) {
      return amount.toFixed(2);
    }
  }
}
</script>
